// modules/pricing/api/pricingApi.ts
import { rpost, rget, DEV_URL } from 'utils/requests';

const PROXY_BASE_URL = `${DEV_URL}/api/PricingProxy`;

const getProxyHeaders = (userToken: string) => ({
  'Content-Type': 'application/json',
  'Authorization': `Bearer ${userToken}`,
});

const AllSelectedDropdownValue = {name: 'All', value: 'All', isSelected: false};

const getUtilityIdentifier = (utility: any) =>
  utility?.EnerexIdentifier ??
  utility?.enerexIdentifier ??
  utility?.enerexUtilityIdenitifier ??
  utility?.id ??
  utility?.value ??
  utility?.LongName ??
  utility?.longName;

const getUtilityName = (utility: any) =>
  utility?.ShortName ??
  utility?.shortName ??
  utility?.LongName ??
  utility?.longName ??
  utility?.utilityName ??
  utility?.name ??
  utility?.value ??
  '';

export const fetchUtilitiesApi = async (params: {
  marketIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const marketIdentifier = params?.marketIdentifier;
  const commodity = params?.commodity || 'Electric';
  if (!params?.userToken) {
    throw new Error('Missing user token for utilities request');
  }

  const payload = {
    marketEnerexIdentifier: marketIdentifier || null,
    commodity: commodity,
  };

  const response = await fetch(
    `${PROXY_BASE_URL}/utilities/market`,
    {
      method: 'POST',
      headers: getProxyHeaders(params.userToken),
      body: JSON.stringify(payload),
      signal: params?.signal,
    }
  );
  if (!response.ok) {
    throw new Error('Failed to fetch utilities by market');
  }

  const data = await response.json();
  const utilitiesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];
  const utilities = utilitiesArray.map((utility: any) => ({
    id: getUtilityIdentifier(utility),
    value: getUtilityIdentifier(utility),
    name: getUtilityName(utility),
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...utilities];
};

export const fetchUtilitiesByStateApi = async (params: {
  stateIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const stateIdentifier = params?.stateIdentifier;
  if (!params?.userToken) {
    throw new Error('Missing user token for utilities-by-state request');
  }
  if (!stateIdentifier) {
    return [AllSelectedDropdownValue];
  }

  const payload = {
    enerexStateIdentifier: stateIdentifier,
    commodity: params?.commodity || 'Electric',
  };
  const response = await fetch(`${PROXY_BASE_URL}/utility`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(payload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch utilities by state');
  }

  const data = await response.json();
  const utilitiesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  const utilities = utilitiesArray.map((utility: any) => ({
    id: getUtilityIdentifier(utility),
    value: getUtilityIdentifier(utility),
    name: getUtilityName(utility),
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...utilities];
};

export const fetchStatesByUtilityApi = async (params: {
  utilityIdentifier?: string;
  commodity?: string;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  const utilityIdentifier = params?.utilityIdentifier;
  if (!params?.userToken) {
    throw new Error('Missing user token for states-by-utility request');
  }
  if (!utilityIdentifier) {
    return [AllSelectedDropdownValue];
  }

  const payload = {
    enerexUtilityIdentifiers: utilityIdentifier,
    commodity: params?.commodity || 'Electric',
  };
  const response = await fetch(`${PROXY_BASE_URL}/state`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(payload),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error('Failed to fetch states by utility');
  }

  const data = await response.json();
  const statesArray = Array.isArray(data)
    ? data
    : Array.isArray(data?.data)
    ? data.data
    : [];

  const states = statesArray.map((item: any) => ({
    id:
      item?.EnerexStateIdentifier ??
      item?.enerexStateIdentifier ??
      item?.id ??
      item?.value ??
      item?.stateName,
    value:
      item?.EnerexStateIdentifier ??
      item?.enerexStateIdentifier ??
      item?.id ??
      item?.value ??
      item?.stateName,
    name: item?.stateName ?? item?.name ??item?.Name ?? item?.value ?? '',
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...states];
};

export const fetchLoadZonesApi = async (params: any): Promise<any> => {

  const enerexStateIdentifier = params?.stateIdentifier;

  if( !enerexStateIdentifier || enerexStateIdentifier === 'All') {
    return [AllSelectedDropdownValue];
  }
  if (!params?.userToken) {
    throw new Error('Missing user token for load zones request');
  }
  const response = await fetch(`${PROXY_BASE_URL}/loadzones`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({ stateIdentifier: enerexStateIdentifier }),
    signal: params?.signal,
});

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error('Failed to fetch loadzones');
  const data = await response.json();
  
  const loadZones = data
    .map((item: any) => ({
      id: item.enerexIdentifier,
      value: item.enerexIdentifier,
      name: item.name,
      isSelected: false,
    }))
    .sort((a: any, b: any) =>
      String(a?.name || "").localeCompare(String(b?.name || ""), undefined, {
        sensitivity: "base",
      })
    );

  return [AllSelectedDropdownValue, ...loadZones];
};

export const fetchCustomerTypeApi = async (params: {
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for customer types request");
  }

  return [
    { id: "All", value: "All", name: "All", isSelected: false },
    { id: "COMM", value: "COMM", name: "Commercial", isSelected: false },
    {
      id: "NONRESI",
      value: "NONRESI",
      name: "Non Residential",
      isSelected: false,
    },
    { id: "RESI", value: "RESI", name: "Residential", isSelected: false },
  ];
};

export const fetchStatesApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error('Missing user token for states request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/states`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal
});

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error('Failed to fetch states list');
  const data = await response.json();
  
  const stateList = data.map((item: any) => ({
    id: item.EnerexStateIdentifier,
    value: item.EnerexStateIdentifier,
    name: item.Name,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...stateList];
};

export const fetchBillingMethodApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error('Missing user token for billing methods request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/billingmethods`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
});

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error('Failed to fetch billing methods');
  const data = await response.json();
  
  const billingMethods = data.map((item: any) => ({
    id: item.shortName,
    value: item.shortName,
    name: item.shortName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...billingMethods];
};

// export const fetchSuppliersApi = async (state: string, utility: string, loadZone: string): Promise<any> => {
//   const response = await fetch(`${BASE_URL}/suppliers?state=${state}&utility=${utility}&loadZone=${loadZone}`);
//   if (!response.ok) throw new Error('Failed to fetch suppliers');
//   return response.json();
// };
const STATIC_COST_COMPONENT_OPTIONS = [
  {
    id: "static-cost-component-all",
    value: "All",
    name: "All",
    isSelected: false,
  },
  {
    id: "static-cost-component-no-pass-through",
    value: "All Inclusive",
    name: "All Inclusive",
    isSelected: false,
  },
];

const mergeStaticCostComponents = (list: any[] = []) => {
  const existingValues = new Set(list.map((item) => item?.value));
  const staticOptions = STATIC_COST_COMPONENT_OPTIONS.filter(
    (option) => !existingValues.has(option.value)
  );
  return [...staticOptions, ...list];
};

export const fetchCostComponentsApi = async (params: {
  commodity?: string | number;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for cost components request");
  }

  const commodityLabel = (() => {

// const commodity = (() => {
//     const c = params?.commodity;
//     if (
//       c === "Electricity" ||
//       c === "Electric" ||
//       c === "electric" ||
//       c === 1 ||
//       c === "1"
//     )
//       return "Electric";
//     if (
//       c === "NatGas" ||
//       c === "Gas" ||
//       c === "Nat Gas" ||
//       c === 2 ||
//       c === "2"
//     )
//       return "NatGas";
//     return "electric"; // default to electric
//   })();
    const c = params?.commodity;
    if (c === 1 || c === "1" || `${c}`.toLowerCase().includes("electric")|| `${c}`.toLowerCase().includes("electricity")) {
      return "Electric";
    }
    if (c === 2 || c === "2" || `${c}`.toLowerCase().includes("gas") || `${c}`.toLowerCase().includes("natgas")) {
      return "NatGas";
    }
    return "Electric";
  })();

  const response = await fetch(`${PROXY_BASE_URL}/bycommodity`, {
    method: "POST",
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({ commodity: commodityLabel }),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch cost components");
  }

  const data = await response.json();

  if (!Array.isArray(data)) {
    return [];
  }

  const normalized = data
    .map((item: any) => ({
      id: item?.enerexIdentifier || item?.name,
      value: item?.name,
      name: item?.name,
      enerexIdentifier: item?.enerexIdentifier,
      isSelected: false,
    }))
    .sort((a: any, b: any) =>
      String(a?.name || a?.value || "").localeCompare(
        String(b?.name || b?.value || ""),
        undefined,
        { sensitivity: "base" }
      )
    );

  return mergeStaticCostComponents(normalized);
};

export const fetchRateClassesApi = async (params: any): Promise<any> => {
  let utilityIdentifier = params?.utilityIdentifier;
  const commodity = params?.commodity ?? '1';

  if (!utilityIdentifier || utilityIdentifier === 'All') {
    return [];
  };

  const apiPayload = {
    enerexUtilityIdentifier: utilityIdentifier,
    commodity,
  };

  if (!params?.userToken) {
    throw new Error('Missing user token for rate classes request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/rateclasses`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(apiPayload),
    signal: params?.signal,
});

  // const response = await fetch(`${BASE_URL}/api/Common/GetUtilityStateComodity?stateId=${stateId}&IsElectric=true`, {
  //   method: 'POST',
  //   headers: getHeaders(token),
  // });
  if (!response.ok) throw new Error('Failed to fetch rateclasses');
  const data = await response.json();
  
  const rateClasses =  data.map((item: any) => ({
    id: item.rateClassName,
    value: item.rateClassName,
    name: item.rateClassName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...rateClasses];
};

export const fetchLoadProfilesApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error('Missing user token for load profiles request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/loadprofiles`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error('Failed to fetch load profiles');
  const data = await response.json();
  
  const loadProfiles = data.map((item: any) => ({
    id: item.value,
    value: item.value,
    name: item.name,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...loadProfiles];
};

/** */

// export const fetchTermBasedPricingChartData = async (isV2=false): Promise<any> => {
//   const postRequestParams = {}
//   const response = await rget(
//     `${DEV_URL}/api/PricingMatrix/GetECToken`,
//     postRequestParams
// );
//   if (response.status != 200) throw new Error('Failed to fetch pricing data');
//   const data = await response.data;
  
//   const chartsData = data.map((chart: any) => {

//     const categories = chart?.dataSets[0]?.dataPoints?.map( item => item?.label);
//     const series = chart?.dataSets?.map(dataset => {
//       const data = dataset?.dataPoints?.map(point => point.value);
//       return {...dataset, data}
//     });
//     return {
//       title: chart?.title,
//       categories,
//       series
//     }
//   });

//   return chartsData;
// };
const getPricingMatrixApiPayload = (params) => {
  const taxIncluded =
    typeof params.taxIncluded === 'boolean' ? params.taxIncluded : null;
  const por = typeof params.por === 'boolean' ? params.por : null;
  // debugger;
  const wrap = (val) => {
    if (Array.isArray(val)) {
      const normalized = val
        .filter(Boolean)
        .map((v) =>
          typeof v === "object" && v !== null
            ? v.value ?? v.id ?? v.name ?? String(v)
            : String(v)
        )
        .map((s) => s.trim())
        .filter((s) => s && s.toLowerCase() !== "all" && s.toLowerCase() !== "none");
      return normalized;
    }
    if (!val) return [];
    const s = String(val).trim();
    return s && s.toLowerCase() !== "all" && s.toLowerCase() !== "none" ? [s] : [];
  };

  // Format date to YYYY-MM-DD format
  const formatDate = (dateString) => {
    if (!dateString) return null;
    try {
      const date = new Date(dateString);
      return date.toISOString().split('T')[0]; // Get YYYY-MM-DD format
    } catch (error) {
      console.error('Error formatting date:', error);
      return null;
    }
  };

  // Check if gas commodity - don't send LoadZone for gas (commodity 2 = gas)
  const isGasCommodity = params.commodity === 2 || params.commodity === '2';
// debugger;
  return {
    Suppliers: [],
    State: params.selectedState,
    Terms: wrap(params.selectedTerm),
    RateClass: wrap(params.selectedRateClass),
    LoadProfile: wrap(params.selectedLoadProfile),
    LoadZone: isGasCommodity ? [] : wrap(params.selectedLoadZone),
    BillingMethod: wrap(params.selectedBillingMethod),
    Utilities: wrap(params.selectedUtility),
    // ProductType: params.productType || '',
    ContractStartFrom: null,
    ContractStartTo: formatDate(params.startToDate) || null,
    EffectiveFrom: formatDate(params.effectiveFromDate) || null,
    EffectiveTo: formatDate(params.effectiveToDate) || null,
    Commodity: params.commodity || '1',
    ProductType: params?.selectedProductType || [],
    CostComponent: params.passThrough || [],
    // Treat 0 and null differently: null means not set, 0 is a valid filter value
    Usage: (params?.usage !== null && params?.usage !== undefined && Number.isFinite(params.usage)) 
      ? Math.floor(params.usage) 
      : null,
    Demand: (params?.demand !== null && params?.demand !== undefined && Number.isFinite(params.demand)) 
      ? Math.floor(params.demand) 
      : null,
    LoadFactor:
      params?.loadFactor !== null &&
      params?.loadFactor !== undefined &&
      Number.isFinite(params.loadFactor)
        ? Number(params.loadFactor)
      : null,
    CustomerClass: params.customerClass || null,
    EnerexClientIdentifier: params.clientId || null,
    Flags: [],
    MarketEnerexIdentifier:params.MarketEnerexIdentifier,
    ...(taxIncluded !== null ? { taxIncluded } : {}),
    ...(por !== null ? { POR: por } : {})
  };
};

export const fetchPriceTrendChartData = async (params: any): Promise<any> => {
  
  const Suppliers = ["6bd2c20f-0ce3-4b00-acde-63569a4273a1", 
    // "374BD0E8-60AC-4A6A-BE86-EF7191E47047", 
    // "38E36FC8-0C9A-4774-9632-555E6C28BB37",
    "958A3FC6-21D4-430F-8834-209E801A7C39",
    // "875C7031-92CB-4ABE-811F-1733A049EBF4",
    "9FEDD1C8-EB97-404C-9E3C-7A267473CCD4", 
    "60bb3692-528d-4b1a-8af9-07e46f55b2b4"]
    .map(id => id.toLowerCase());

  const apiPayload = getPricingMatrixApiPayload({Suppliers ,...params});
  if (!params?.userToken) {
    throw new Error('Missing user token for price ranking request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/priceranking`, {
      method: 'POST',
      headers: getProxyHeaders(params.userToken),
      body: JSON.stringify(apiPayload),
      signal: params?.signal,
  });

  if (!response.ok) {
      throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  let jsonData = await response.json();
  if(jsonData){ 
    jsonData = jsonData.map(item => ({
          ...item,
          Suppliers: (() => {
            const suppliers = item.Suppliers.filter(
              supplier => supplier.Supplier !== null
            );
            const hasIsLiveFlag = suppliers.some(
              supplier => Object.prototype.hasOwnProperty.call(supplier || {}, "IsLive")
            );
            return hasIsLiveFlag
              ? suppliers.filter(supplier => supplier.IsLive === true)
              : suppliers;
          })(),
      }));

  }
  const sortedSuppliers = jsonData;
  // .sort(
  //   (a,b) => a.Supplier.localeCompare(b.Supplier)
  // );

  return sortedSuppliers;
}

export const fetchPriceTrendMinMaxChartData = async (params: any): Promise<any> => {
  
  const apiPayload = getPricingMatrixApiPayload(params);
  if (!params?.userToken) {
    throw new Error('Missing user token for pricing range request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/pricingrange`, {
      method: 'POST',
      headers: getProxyHeaders(params.userToken),
      body: JSON.stringify(apiPayload),
      signal: params?.signal,
  });

  if (!response.ok) {
      throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  return await response.json();
}

export const fetchForwardCurveChartData = async (params: any): Promise<any> => {
  const Suppliers = ["6bd2c20f-0ce3-4b00-acde-63569a4273a1", 
    "958A3FC6-21D4-430F-8834-209E801A7C39",
    // "374BD0E8-60AC-4A6A-BE86-EF7191E47047", 
    // "38E36FC8-0C9A-4774-9632-555E6C28BB37",
    // "875C7031-92CB-4ABE-811F-1733A049EBF4",
    "9FEDD1C8-EB97-404C-9E3C-7A267473CCD4", 
    "60bb3692-528d-4b1a-8af9-07e46f55b2b4"]
    .map(id => id.toLowerCase());

  const apiPayload = getPricingMatrixApiPayload({Suppliers ,...params});

  if (!params?.userToken) {
    throw new Error('Missing user token for forward curve request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/forwardcurve`, {
      method: 'POST',
      headers: getProxyHeaders(params.userToken),
      body: JSON.stringify(apiPayload),
      signal: params?.signal,
  });

  if (!response.ok) {
      throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  const jsonData = await response.json();
  const sortedSuppliers = jsonData;
  // .sort(
  //   (a,b) => a.Supplier.localeCompare(b.Supplier)
  // );
  return sortedSuppliers;
}

export const fetchForwadCurveMinMaxChartData = async (params: any): Promise<any> => {
  
  const apiPayload = getPricingMatrixApiPayload(params);

  if (!params?.userToken) {
    throw new Error('Missing user token for forward range request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/forwardcurverange`, {
      method: 'POST',
      headers: getProxyHeaders(params.userToken),
      body: JSON.stringify(apiPayload),
      signal: params?.signal,
  });

  if (!response.ok) {
      throw new Error(`Price trend fetch failed: ${response.status}`);
  }

  return await response.json();
}

export const fetchMarketsApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error('Missing user token for markets request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/markets`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify(params.commodity ? { commodity: params.commodity } : {}),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error('Failed to fetch markets');
  const data = await response.json();
  // debugger;
  const markets = data.Markets?.map((item: any) => ({
    id: item.Market,
    value: item.EnerexIdentifier,
    name: item.Market,
    commodity: item.Commodity,
    isSelected: false,
    // marketIdentifier:item.EnerexIdentifier
  })) || [];

  return markets;
};

export const fetchProductTypeApi = async (params: {
  commodity?: string | number;
  userToken: string;
  signal?: AbortSignal;
}): Promise<any[]> => {
  if (!params?.userToken) {
    throw new Error("Missing user token for product types request");
  }

  // Map commodity to string value (Electric or NatGas)
  const commodity = (() => {
    const c = params?.commodity;
    if (c === 1 || c === "1" || `${c}`.toLowerCase().includes("electric") || `${c}`.toLowerCase().includes("electricity")) {
      return "Electric";
    }
    if (c === 2 || c === "2" || `${c}`.toLowerCase().includes("gas") || `${c}`.toLowerCase().includes("natgas")) {
      return "NatGas";
    }
    return "Electric"; // default to Electric
  })();

  const response = await fetch(`${PROXY_BASE_URL}/producttypes/${commodity}`, {
    method: "GET",
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });

  if (!response.ok) {
    throw new Error("Failed to fetch product types");
  }

  const data = await response.json();

  // Handle different response formats
  const productTypesArray = Array.isArray(data) ? data : Array.isArray(data?.data) ? data.data : [];

  // Transform to expected format
  const productTypes = productTypesArray.map((item: any) => {
    // Handle string values
    if (typeof item === "string") {
      return {
        id: item,
        value: item,
        name: item,
        isSelected: false,
      };
    }
    // Handle object format with EnerexIdentifier, Name, Commodity
    return {
      id: item?.EnerexIdentifier || item?.id,
      value: item?.Name || item?.name || item?.value,
      name: item?.Name || item?.name || item?.value,
      isSelected: false,
    };
  });

  return productTypes;
};

export const fetchStatesByMarketApi = async (params: any): Promise<any> => {
  const marketEnerexIdentifier = params?.marketEnerexIdentifier || '';
  const commodity = params?.commodity || '';
  if (!marketEnerexIdentifier) return [AllSelectedDropdownValue];

  if (!params?.userToken) {
    throw new Error('Missing user token for market states request');
  }
  const response = await fetch(`${PROXY_BASE_URL}/marketstates`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    body: JSON.stringify({
      marketEnerexIdentifier,
      commodity,
    }),
    signal: params?.signal,
  });

  if (!response.ok) throw new Error('Failed to fetch states by market');
  const data = await response.json();

  // Handle case where data might not be an array
  let statesArray = [];
  if (Array.isArray(data)) {
    statesArray = data;
  } else if (data && Array.isArray(data.states)) {
    statesArray = data.states;
  } else if (data && Array.isArray(data.data)) {
    statesArray = data.data;
  }

  const stateList = statesArray.map((item: any) => ({
    id: item.enerexStateIdentifier,
    value: item.enerexStateIdentifier,
    name: item.stateName,
    isSelected: false,
  }));

  return [AllSelectedDropdownValue, ...stateList];
};

export const fetchSuppliersListApi = async (params: any): Promise<any> => {
  if (!params?.userToken) {
    throw new Error('Missing user token for suppliers list request');
  }

  const response = await fetch(`${PROXY_BASE_URL}/suppliers`, {
    method: 'POST',
    headers: getProxyHeaders(params.userToken),
    signal: params?.signal,
  });
  if (!response.ok) throw new Error('Failed to fetch suppliers list');
  const data = await response.json();
  return data.map((item: any) => ({
    id: item.SupplierIdentifier,
    value: item.SupplierIdentifier,
    name: item.SupplierName,
    isSelected: false,
  }));
};
